import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Primary, White } from '../../styles/Colors';
import { FontFamily } from '../../styles/Typography';
import { ButtonProps } from './Button';
import { ButtonGroupProps } from './ButtonGroup';
import { BORDER_RADIUS, FONT_SIZE } from './const';
import { ButtonBaseProps } from './types';

interface StyledButtonProps extends ButtonProps {
  fontSize: string;
}

interface StyledButtonBaseProps extends ButtonBaseProps {
  fontSize: string;
  isFillCurrentColor?: boolean;
}

/**
 * The style both `StyledButton` and `StyledIconButton` compose — so anything
 * declared here reaches every Button- and IconButton-based control in the app,
 * and nothing else (`Select`, `Stepper` and `Slider` roll their own).
 *
 * FOCUS RING (ISSUE-043, WCAG 2.1 SC 2.4.7 AA). The reset below clears the UA
 * ring with `outline: none`, so the `&:focus-visible` rule further down is its
 * ONLY replacement. It is deliberately app-wide: ISSUE-004 restored the ring
 * scoped to the crash-fallback alone and deferred this decision, and a
 * per-component opt-in would leave the same hole open for every control nobody
 * remembers to opt in.
 *
 * `:focus-visible`, never bare `:focus`, so a mouse click leaves no ring
 * behind. Emotion emits the pseudo-class verbatim and the app targets Chromium
 * (NFR-013), so no polyfill and no `:focus:not(:focus-visible)` fallback is
 * needed — none is added speculatively.
 *
 * RING COLOUR AND OFFSET (ISSUE-066). The ring is `2px solid White` drawn
 * INSIDE the border box (`outline-offset: -2px`). Both halves of that changed
 * together, and each fixes what the other used to make impossible.
 *
 * ISSUE-043 shipped Primary[200] (#6798ff) with a POSITIVE 2px offset, because
 * #6798ff on the contained variant's own Primary[400] (#0a5bff) fill is
 * 1.90:1. The offset moved the adjacent colour to the surface behind the
 * button — which fixed the fill and broke on the surfaces, three of them
 * measured and recorded: PastelBlue #3d8dd7 = 1.25:1 (StyledAudioEditor.tsx:17
 * paints the whole editor this colour and `.editor-zoom button` at :203-205 is
 * transparent, so BOTH sides of the ring were PastelBlue — six IconButtons);
 * Grey450 #5d5d5d = 2.36:1 (`.target-audio-files`,
 * StyledVoiceFileContent.tsx:19, and the `.target-group` / `.simple-group`
 * rows, StyledList.tsx:86,189); and a positive offset is CLIPPED by an
 * ancestor's `overflow: hidden` at three sites.
 *
 * White dissolves all of it, and the search that found it is worth recording
 * because the previous conclusion was wrong. ISSUE-066's Evidence asserted
 * that "no single palette colour can satisfy 1.4.11 app-wide"; that came from
 * evaluating #6798ff against 30 palette values, i.e. holding the RING fixed
 * and varying the surface. Searching the other axis — all 35 palette colours
 * against the five surfaces buttons actually sit on — returns THREE that pass:
 * #ffffff (worst 3.51:1), #f4f4f4 (3.19), #f3f3ff (3.18). Measured:
 *   PastelBlue 3.51 · Grey450 6.58 · Grey500 9.74 · Black 18.73 ·
 *   PureBlack 21.00 · Primary[400] fill 5.31 — all >= 3:1.
 *
 * Because White passes against the FILL too (5.31:1), the offset stops being
 * load-bearing, and that is what lets it go negative. An inset ring has zero
 * outward footprint, so it cannot be clipped by any ancestor — which retires
 * the whole clipping problem instead of chasing ~4px of clearance across three
 * layouts. ISSUE-066's Note claimed an inside ring was "precisely what the
 * 1.90:1 fill result forbids": true of #6798ff, not of White.
 *
 * The one thing White does NOT fix is group opacity — see the `:hover` rules
 * below.
 */
const commonButtonStyle = <T extends StyledButtonBaseProps>({
  radius = 'md',
  variant = 'contained',
  color = Primary['400'],
  fontSize = FONT_SIZE['md'],
  isFillCurrentColor = true,
}: T) => css`
  /* reset button */
  margin: 0;
  background: none;
  border: none;
  outline: none;
  /* button style */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 150%;
  font-family: ${FontFamily['Inter']};
  border-radius: ${BORDER_RADIUS[radius]};
  ${variant === 'contained'
    ? css`
        border: 1px solid ${color};
        background-color: ${color};
        color: ${White};
      `
    : variant === 'outlined'
    ? css`
        border: 1px solid ${color};
        color: ${color};
      `
    : css`
        color: ${color};
      `}
  // TODO: Add hover, disabled style based on Design System
    /* button hover style */
    &:hover {
    opacity: 0.8;
  }
  /* button keyboard focus style — ISSUE-043, retuned by ISSUE-066 */
  &:focus-visible {
    outline: 2px solid ${White};
    outline-offset: -2px;
  }
  /* opacity on the element is GROUP opacity: it fades the outline with the
     button. Over PastelBlue the white ring composites to 2.81:1 and FAILS
     1.4.11 — and it bites hardest on list rows whose IconButtons only become
     visible on hover, i.e. exactly when hover and focus co-occur. There is no
     way to exempt an outline from element opacity, so the fade is dropped
     while the control is keyboard-focused. */
  &:hover:focus-visible {
    opacity: 1;
  }
  /* button disabled style */
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  ${isFillCurrentColor &&
  css`
    & svg * {
      fill: currentColor;
    }
  `}
  svg: {
    width: ${fontSize};
    height: ${fontSize};
  }
`;

export const StyledButton = styled.button(
  <T extends StyledButtonProps>({
    fullWidth,
    radius,
    variant,
    color,
    fontSize,
    fontWeight,
    startIcon,
    endIcon,
  }: T) => {
    return css`
      ${commonButtonStyle({ radius, variant, color, fontSize })}
      padding: 0.25rem calc(${fontSize} - 0.125rem);
      width: ${fullWidth ? '100%' : 'auto'};
      font-size: ${fontSize};
      font-weight: ${fontWeight};
      /* button icon */
      .sup-button-icon {
        width: calc(${fontSize} + 0.4rem);
        height: calc(${fontSize} + 0.4rem);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-right: ${startIcon ? '0.4rem' : '0'};
        margin-left: ${endIcon ? '0.4rem' : '0'};
      }
    `;
  }
);

export const StyledIconButton = styled.button(
  <T extends StyledButtonBaseProps>({
    radius,
    variant,
    color,
    fontSize,
    isFillCurrentColor,
  }: T) => {
    return css`
      ${commonButtonStyle({
        radius,
        variant,
        color,
        fontSize,
        isFillCurrentColor,
      })}
      padding: 0;
      box-sizing: border-box;
      width: calc(${fontSize} + 0.375rem);
      height: calc(${fontSize} + 0.375rem);
      font-size: ${fontSize};
    `;
  }
);

export const StyledButtonGroup = styled.div(
  <T extends ButtonGroupProps>({ radius = 'md' }: T) => {
    return css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      button {
        margin: 0;
        border-radius: 0;
        &:first-of-type {
          border-top-left-radius: ${BORDER_RADIUS[radius]};
          border-bottom-left-radius: ${BORDER_RADIUS[radius]};
        }
        &:last-of-type {
          border-top-right-radius: ${BORDER_RADIUS[radius]};
          border-bottom-right-radius: ${BORDER_RADIUS[radius]};
        }
        &:not(:first-of-type) {
          margin-left: -1px;
        }
      }
    `;
  }
);
